/**
 * 연동(same-value) 인덱스의 임베드 형태.
 *
 * 뷰어가 섹션 청크로 인덱스를 다시 만들면 라이브와 두 가지가 갈린다 — 대사에 매핑되지
 * 않은 표의 연동이 통째로 빠지고(빈자리가 "연동 없음"으로 읽힌다), 청크 소스가 달라
 * 표시값의 부호·서식이 섞인다(대사 응답은 negation/formatting 적용, editor preview 는
 * 미적용). 그래서 라이브와 같은 입력으로 export 시점에 만들어 그대로 싣는다.
 *
 * 뷰어가 실제로 쓰는 두 맵만 담는다(연동 판정과 위치 목록). Map 은 JSON 이 못 실어
 * entries 로 눕힌다.
 *
 * `CellIdentityKey` 는 metadata 9개 필드의 JSON 문자열(네임스페이스 URL 포함)이라 키
 * 하나가 수백 바이트다. 위치마다 자기 키를 또 들고 있으면 같은 문자열이 위치 수만큼
 * 반복되고, HTML 에 박힐 때 따옴표 escape 로 한 번 더 불어난다. 위치의 `identityKey` 는
 * 맵 키에서 복원 가능하므로 직렬화에서 뺀다.
 */

import type {
  CellIdentityIndex,
  CellIdentityLocation,
} from "@/utils/ixd-cell-model";

/** 직렬화된 위치 — `identityKey` 는 맵 키가 들고 있어 뺀다. */
type SerializedLocation = Omit<CellIdentityLocation, "identityKey">;

export interface SerializedLinkageIndex {
  identityKeyByNormalizedCellId: [string, string][];
  identityLocationsByKey: [string, SerializedLocation[]][];
}

export type LinkageIndex = Pick<
  CellIdentityIndex,
  "identityKeyByNormalizedCellId" | "identityLocationsByKey"
>;

export const serializeLinkageIndex = (
  index: LinkageIndex,
): SerializedLinkageIndex => ({
  identityKeyByNormalizedCellId: Array.from(
    index.identityKeyByNormalizedCellId,
  ),
  identityLocationsByKey: Array.from(index.identityLocationsByKey).map(
    ([key, locations]) => [
      key,
      locations.map(({ identityKey: _omitted, ...rest }) => rest),
    ],
  ),
});

export const deserializeLinkageIndex = (
  data: SerializedLinkageIndex,
): LinkageIndex => ({
  identityKeyByNormalizedCellId: new Map(data.identityKeyByNormalizedCellId),
  identityLocationsByKey: new Map(
    data.identityLocationsByKey.map(([key, locations]) => [
      key,
      locations.map((location) => ({ ...location, identityKey: key })),
    ]),
  ),
});
